import { BaseService } from "@services/BaseService";
import { useState } from "react";
import environment from "environment";
import { Customer } from "@interfaces/customer";
import { toast } from "@ucchub/ui";
import { t } from "i18next";
import { CustomerDetails } from "@components/customer/Customer.types";
import toQueryString from "src/utils/queryString";
import type { AxiosResponse } from "axios";
import { isString, pickBy } from "lodash";

const getErrorResponse = (response: AxiosResponse) => {
  if (response.status === 403) {
    return t("core:customers:error_permission");
  }
  if (
    response.data.errorCode === "IR0001" ||
    response.data.errorCode === "IR0002"
  ) {
    return t("core:customers:hooks.invalid_payload");
  } else {
    return response.data.errorCode;
  }
};

const getUpdatedDetails = (
  newDetails: Omit<Customer, "id">,
  oldDetails?: CustomerDetails
) => {
  if (!oldDetails) {
    return newDetails;
  }

  const updatedDetails = {} as Omit<Customer, "id">;

  if (oldDetails.name !== newDetails.name) {
    updatedDetails.name = newDetails.name;
  }
  if (oldDetails.addressLine1 !== newDetails.addressLine1) {
    updatedDetails.addressLine1 = newDetails.addressLine1;
  }
  if (oldDetails.addressLine2 !== newDetails.addressLine2) {
    updatedDetails.addressLine2 = newDetails.addressLine2;
  }
  if (oldDetails.country !== newDetails.country) {
    updatedDetails.country = newDetails.country;
  }
  if (oldDetails.city !== newDetails.city) {
    updatedDetails.city = newDetails.city;
  }
  if (oldDetails.area !== newDetails.area) {
    updatedDetails.area = newDetails.area;
  }
  if (oldDetails.postcode !== newDetails.postcode) {
    updatedDetails.postcode = newDetails.postcode;
  }
  if (oldDetails.phoneNumber !== newDetails.phoneNumber) {
    updatedDetails.phoneNumber = newDetails.phoneNumber;
  }
  if (oldDetails.contactFirstName !== newDetails.contactFirstName) {
    updatedDetails.contactFirstName = newDetails.contactFirstName;
  }
  if (oldDetails.contactLastName !== newDetails.contactLastName) {
    updatedDetails.contactLastName = newDetails.contactLastName;
  }
  if (oldDetails.contactLastName !== newDetails.contactLastName) {
    updatedDetails.contactLastName = newDetails.contactLastName;
  }
  if (oldDetails.contactEmail !== newDetails.contactEmail) {
    updatedDetails.contactEmail = newDetails.contactEmail;
  }
  if (oldDetails.pid !== newDetails.pid) {
    updatedDetails.pid = newDetails.pid;
  }
  if (oldDetails.status !== newDetails.status) {
    updatedDetails.status = newDetails.status;
  }
  return updatedDetails;
};
const useCustomerCreateEdit = () => {
  const [error, setError] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [customerAdded, setCustomerAdded] = useState(false);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>();
  const [isCustomerEditing, setIsCustomerEditing] = useState(false);

  const addCustomer = (data: Customer, isDraft?: boolean, submit?: boolean) => {
    setLoading(true);

    const dataWithoutEmptyString = pickBy(
      data,
      (value) => !isString(value) || (isString(value) && value.length > 0)
    );

    return BaseService.post(
      `${environment.api.customer}`,
      dataWithoutEmptyString
    )
      .then((res) => {
        if (res.status === 201) setSubmitSuccess(true);
        setCustomerAdded(true);
        if (isDraft || submit) {
          toast({
            text: t(
              "core:customers:hooks.customer.draft_customer.customer_updated"
            ),
            state: t(
              "core:customers:hooks.customer.draft_customer.update_state"
            ),
          });
        }
        return { status: "success", customerData: res.data };
      })
      .catch(({ response }) => {
        toast({
          text: getErrorResponse(response),
          state: "error",
        });
        return { status: "error", customerData: undefined };
      })
      .finally(() => setLoading(false));
  };

  const isCustomerNameExist = async (name?: string) => {
    const queryString = toQueryString({
      name,
    });
    const url = environment.api.customer + `?${queryString}`;
    return await BaseService.get(url)
      .then(({ data }) => {
        if (data?.length > 0) {
          return (
            data.findIndex(
              (customer: { name: string | undefined }) => customer.name === name
            ) > -1
          );
        }
        return false;
      })
      .catch(() => false);
  };

  const updateCustomerDetails = (
    customerId: string | undefined,
    newDetails: Omit<Customer, "id">
  ) => {
    setIsCustomerEditing(true);
    setLoading(true);
    return BaseService.patch(
      environment.api.customer + `/${customerId}?submit=true`,
      newDetails
    )
      .then((res) => {
        setCustomerDetails(res.data);
        return { status: "success" };
      })
      .catch(({ response }) => {
        toast({
          text: getErrorResponse(response),
          state: "error",
        });
        setError(response.data.errorCode);
        return { status: "error" };
      })
      .finally(() => {
        setLoading(false);
        setIsCustomerEditing(false);
      });
  };
  const isPIDNameExist = async (pid?: string) => {
    const queryString = toQueryString({
      pid,
    });
    const url = environment.api.customer + `?${queryString}`;
    return await BaseService.get(url)
      .then(({ data }) => {
        if (data?.length > 0) {
          return (
            data.findIndex(
              (customer: { pid: string | undefined }) => customer.pid === pid
            ) > -1
          );
        }
        return false;
      })
      .catch(() => false);
  };

  const updateCustomer = (
    data: Customer,
    customerId?: string,
    isEdit?: boolean,
    isDraft?: boolean,
    oldCustomerData?: CustomerDetails
  ) => {
    setIsCustomerEditing(true);
    setLoading(true);
    const updateDetails = getUpdatedDetails(data, oldCustomerData);
    const url =
      environment.api.customer +
      `/${customerId}` +
      (isDraft && isEdit ? "?submit=true" : "");
    return BaseService.patch(url, updateDetails)
      .then((res) => {
        setCustomerDetails(res.data);
        if (isDraft && !isEdit) {
          toast({
            text: t(
              "core:customers:hooks.customer.draft_customer.customer_updated"
            ),
            state: t(
              "core:customers:hooks.customer.draft_customer.update_state"
            ),
          });
        }

        return { status: "success", customer: res.data };
      })
      .catch(({ response }) => {
        toast({
          text: getErrorResponse(response),
          state: "error",
        });
        setError(response.data.errorCode);
        return { status: "error" };
      })
      .finally(() => {
        setLoading(false);
        setIsCustomerEditing(false);
      });
  };
  return {
    addCustomer,
    error,
    isLoading,
    customerAdded,
    isCustomerNameExist,
    updateCustomer,
    isCustomerEditing,
    updateCustomerDetails,
    submitSuccess,
    customerDetails,
    isPIDNameExist,
  };
};
export default useCustomerCreateEdit;
